import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AviationEvent, AviationEventTypeDesc, AviationEventType, TimeZones } from '../../shared/models/aviation-event.model';
import { MAT_DIALOG_DATA, MatDatepickerInputEvent, MatDialogRef, MatSnackBar } from '@angular/material';
import { AviationEventsService } from '../../shared/services/aviation-events.service';
import { eventHours, eventMinutes, eventAMPM } from '../../globals';
import * as MomentTZ from 'moment-timezone';


@Component({
  selector: 'app-edit-event-details',
  templateUrl: './edit-event-details.component.html',
  styleUrls: ['./edit-event-details.component.scss']
})
export class EditEventDetailsComponent implements OnInit {
  formGroup: FormGroup;
  aviationEvent: AviationEvent;
  isLoading = true;
  eventTypes: String[];
  startDateMin: Date = new Date();
  endDateMin: Date = this.startDateMin;
  timeZones = Object.keys(TimeZones).sort();
  // tslint:disable-next-line:max-line-length
  urlRegex = /^((https?|http):\/\/)?(([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}|localhost)(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(\#[-a-z\d_]*)?$/i;
  eventStart: MomentTZ;
  eventEnd: MomentTZ;
  eventStartDateString: string;
  eventStartTimeString: string;
  eventEndDateString: string;
  eventEndTimeString: string;
  startDate: Date;
  endDate: Date;
  eventHours = eventHours;
  eventMinutes = eventMinutes;
  eventAMPM = eventAMPM;
  startHour: string;
  startMin: string;
  startAMPM: string;
  endHour: string;
  endMin: string;
  endAMPM: string;


  constructor(private _matDialogRef: MatDialogRef<EditEventDetailsComponent>,
              @Inject(MAT_DIALOG_DATA) data,
              private _aviationEventsService: AviationEventsService,
              private _fb: FormBuilder,
              private _snackBar: MatSnackBar) {
    this.aviationEvent = data.aviationEvent;
  }

  ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this._matDialogRef.close(null);
    });
    const currentTimeZoneSimpleName =  Object.keys(TimeZones).filter(it => TimeZones[it] === this.aviationEvent.timezone).pop();

    this.eventTypes = Object.values(AviationEventTypeDesc) as String[];
    const currentEventType = AviationEventTypeDesc[AviationEventType[this.aviationEvent.typeId]];
    this.eventStart = MomentTZ.tz(this.aviationEvent.startDateTime, 'UTC');
    this.eventStartDateString = this.eventStart.clone().tz(this.aviationEvent.timezone).format('dddd, MMMM Do, YYYY');
    this.eventStartTimeString = this.eventStart.clone().tz(this.aviationEvent.timezone).format('HH:mm');
    this.eventEnd = MomentTZ.tz(this.aviationEvent.endDateTime, 'UTC');
    this.eventEndDateString = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('dddd, MMMM Do, YYYY');
    this.eventEndTimeString = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('HH:mm');
    this.startDate = this.eventStart.clone().tz(this.aviationEvent.timezone).toDate();
    this.endDate = this.eventEnd.clone().tz(this.aviationEvent.timezone).toDate();
    this.startHour = this.eventStart.clone().tz(this.aviationEvent.timezone).format('h');
    this.startMin = this.eventStart.clone().tz(this.aviationEvent.timezone).format('mm');
    this.startAMPM = this.eventStart.clone().tz(this.aviationEvent.timezone).format('A');
    this.endHour = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('h');
    this.endMin = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('mm');
    this.endAMPM = this.eventEnd.clone().tz(this.aviationEvent.timezone).format('A');


    this.formGroup = this._fb.group({
      startDate: [this.startDate, Validators.required],
      endDate: [this.endDate, Validators.required],
      startHour: [this.startHour, Validators.required],
      startMin: [this.startMin, Validators.required],
      startAMPM: [this.startAMPM, Validators.required],
      endHour: [this.endHour, Validators.required],
      endMin: [this.endMin, Validators.required],
      endAMPM: [this.endAMPM, Validators.required],
      timezone: [currentTimeZoneSimpleName || '', Validators.required],
      webinarRegistrationLink: [this.aviationEvent.webinarRegistrationLink,
        [Validators.maxLength(256), Validators.pattern(this.urlRegex)]],
      informationWebsite: [this.aviationEvent.informationWebsite,
        [Validators.maxLength(256), Validators.pattern(this.urlRegex)]],
      websiteCustomLabel: [this.aviationEvent.websiteCustomLabel, [Validators.maxLength(50)]],
      type: [currentEventType, [Validators.maxLength(256)]],
      cost: [this.aviationEvent.cost || 'Unknown', [Validators.maxLength(256)]]
    }, {updateOn: 'submit'});
    this.isLoading = false;
  }

  showDateError(): boolean {
    const startDate: Date = this.formGroup.value.startDate;
    startDate.setHours(0, 0, 0, 0);
    const endDate: Date = this.formGroup.value.endDate;
    endDate.setHours(0, 0, 0, 0);
    let startHour: number = +this.formGroup.value.startHour;
    if (startHour === 12) {
      startHour = 0;
    }
    startHour = this.formGroup.value.startAMPM === 'PM' ? startHour + 12 : startHour;
    let endHour: number = +this.formGroup.value.endHour;
    if (endHour === 12) {
      endHour = 0;
    }
    endHour = this.formGroup.value.endAMPM === 'PM' ? endHour + 12 : endHour;

    const dateBoolean = startDate && endDate && startDate.getTime() > endDate.getTime();
    if (startDate && endDate && startDate.getTime() === endDate.getTime()) {
      return startHour > endHour || (startHour === endHour && +this.formGroup.value.startMin > +this.formGroup.value.endMin);
    }
    return dateBoolean;
  }

  eventDateChange(type: string, event: MatDatepickerInputEvent<Date>) {
    const element = event.targetElement;
    if (element.getAttribute('name') === 'startDate') {
      this.endDateMin = event.value;
    }
  }

  private buildZonedTime(date: Date, time: string, timezone: String): string {
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const year = date.getFullYear();
    const localTime = MomentTZ.tz(`${year}-${month}-${day} ${time}`, timezone);
    return localTime.utc().format('MM/DD/YYYY h:mm:ss A');
  }

  close(result = {cancelBtnClicked: true, success: false}) {
    if (result) {
      this._matDialogRef.close(result);
    }
  }

  cancel() {
    this._matDialogRef.close(null);
  }

  async save () {
    const TEMP_aviationEvent = this.aviationEvent;
    const tzName = TimeZones[this.formGroup.value.timezone];
    const startDate = new Date(this.formGroup.value.startDate);
    let startTime = +this.formGroup.value.startHour < 10 ? '0' + this.formGroup.value.startHour : this.formGroup.value.startHour;
    // if startTime is 12:00 AM, set to 00:00
    if (+this.formGroup.value.startHour === 12 && this.formGroup.value.startAMPM === 'AM') {
      startTime = '00';
    } else {
      startTime = this.formGroup.value.startAMPM === 'PM' && +this.formGroup.value.startHour < 12 ? (+startTime + 12).toString() :
        startTime;
    }
    startTime = startTime + ':' + this.formGroup.value.startMin;
    const endDate = new Date(this.formGroup.value.endDate);
    let endTime = +this.formGroup.value.endHour < 10 ? '0' + this.formGroup.value.endHour : this.formGroup.value.endHour;
    // if endTime is 12:00 AM, set to 00:00
    if (+this.formGroup.value.endHour === 12 && this.formGroup.value.endAMPM === 'AM') {
      endTime = '00';
    } else {
      endTime = this.formGroup.value.endAMPM === 'PM' && +this.formGroup.value.endHour < 12 ? (+endTime + 12).toString() : endTime;
    }
    endTime = endTime + ':' + this.formGroup.value.endMin;

    if (this.formGroup.invalid) {
      this._snackBar.open('Form field validation failed. check form values', null, {duration: 5000});
      return;
    } else if (this.showDateError()) {
      this._snackBar.open('Start time cannot be after end time, check end time is not before start time', null, {duration: 5000});
      return;
    }

    if (this.formGroup.value.webinarRegistrationLink && this.formGroup.value.webinarRegistrationLink.trim().length) {
      this.aviationEvent.webinarRegistrationLink = this.formGroup.value.webinarRegistrationLink.trim();
    } else {
      this.aviationEvent.webinarRegistrationLink = null;
    }
    if (this.formGroup.value.informationWebsite && this.formGroup.value.informationWebsite.trim().length) {
      this.aviationEvent.informationWebsite = this.formGroup.value.informationWebsite.trim();
    } else {
      this.aviationEvent.informationWebsite = null;
    }
    if (this.formGroup.value.websiteCustomLabel && this.formGroup.value.websiteCustomLabel.trim().length) {
      this.aviationEvent.websiteCustomLabel = this.formGroup.value.websiteCustomLabel.trim();
    } else {
      this.aviationEvent.websiteCustomLabel = null;
    }
    if (this.formGroup.value.cost && this.formGroup.value.cost.trim().length && this.formGroup.value.cost.trim() !== 'Unknown') {
      this.aviationEvent.cost = this.formGroup.value.cost.trim();
    } else {
      this.aviationEvent.cost = null;
    }
    // this.aviationEvent.format = this.formGroup.value.format;
    this.aviationEvent.type = AviationEvent.parseEventFromString(this.formGroup.value.type);
    this.aviationEvent.typeId = this.aviationEvent.type;
    this.aviationEvent.timezone = tzName;
    this.aviationEvent.startDateTimeUTC = this.buildZonedTime(startDate, startTime, tzName);
    this.aviationEvent.endDateTimeUTC = this.buildZonedTime(endDate, endTime, tzName);

    // make api call
    try {
      await this._aviationEventsService.updateAviationEvent(this.aviationEvent);
      this.close({cancelBtnClicked: false, success: true});
    } catch (e) {
      this.aviationEvent = TEMP_aviationEvent;
      this.close({cancelBtnClicked: false, success: false});
    }
  }
}
